/**
 * Контекст «шапки контента» оболочки (§2.2): хлебная крошка + заголовок
 * страницы (h2) + опциональный правый хинт.
 *
 * Глобальной полосы email/поиск/колокольчик СВЕРХУ НЕТ (убрана в v2.2).
 * Вместо неё — лёгкая строка внутри контента: «Группа / Страница» +
 * заголовок (например, имя пользователя на профиле).
 *
 * По умолчанию крошка/заголовок выводятся из активного пункта канонической
 * навигации (group/item label). Страница может переопределить заголовок и
 * правый хинт через `useSetShellHeader` (напр. профиль ставит имя
 * пользователя как h2).
 *
 * Реализация разнесена на два контекста: значение (для AppShell) и СТАБИЛЬНЫЙ
 * dispatch (для страниц), чтобы `useSetShellHeader` не зависел от меняющегося
 * объекта значения и не уходил в бесконечный цикл ре-рендера.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface ShellHeaderOverride {
  /** Заголовок страницы (h2). Если не задан — берётся лейбл пункта nav. */
  title?: string;
  /** Правый хинт в строке шапки (опционально). */
  hint?: ReactNode;
}

type SetFn = (o: ShellHeaderOverride) => void;

const ValueContext = createContext<ShellHeaderOverride>({});
const SetContext = createContext<SetFn>(() => undefined);

export function ShellHeaderProvider({ children }: { children: ReactNode }): JSX.Element {
  const [override, setOverrideState] = useState<ShellHeaderOverride>({});
  // Стабильный setter — ссылка не меняется между рендерами.
  const setOverride = useCallback<SetFn>((o) => setOverrideState(o), []);
  const value = useMemo(() => override, [override]);
  return (
    <SetContext.Provider value={setOverride}>
      <ValueContext.Provider value={value}>{children}</ValueContext.Provider>
    </SetContext.Provider>
  );
}

/** Текущее переопределение шапки (для AppShell). */
export function useShellHeaderOverride(): ShellHeaderOverride {
  return useContext(ValueContext);
}

/**
 * Страница задаёт свой заголовок/хинт шапки. Сбрасывает на размонтировании.
 * Зависит ТОЛЬКО от стабильного setter и строкового title; hint берётся из
 * ref (ReactNode нестабилен по ссылке) — это исключает бесконечный цикл.
 */
export function useSetShellHeader(override: ShellHeaderOverride): void {
  const setOverride = useContext(SetContext);
  const hintRef = useRef(override.hint);
  hintRef.current = override.hint;
  const title = override.title;
  useEffect(() => {
    setOverride({ ...(title !== undefined ? { title } : {}), hint: hintRef.current });
    return () => setOverride({});
  }, [setOverride, title]);
}
